/**
 * Wishlist Authentication Manager
 * Handles user authentication for wishlist system
 */

class WishlistAuth {
  constructor() {
    // Wait for WishlistAPI to be available
    if (!window.WishlistAPI) {
      console.error('WishlistAPI not loaded yet');
      return;
    }
    this.api = window.WishlistAPI;
    this.isAuthenticated = false;
    this.currentUser = null;
    this.init();
  }

  async init() {
    // Check if user has existing token
    if (this.api.getToken()) {
      await this.verifyToken();
    }

    // Listen for auth required events
    document.addEventListener('wishlist:auth:required', () => {
      this.showAuthPrompt();
    });
  }

  /**
   * Get Shopify customer data if logged in
   */
  async getShopifyCustomer() {
    try {
      // Check if customer account API is available
      if (window.Shopify?.customerAccount) {
        const customer = await window.Shopify.customerAccount.getCustomer();
        return customer;
      }
      
      // Fallback: check for customer data in page
      const customerData = document.querySelector('[data-customer-data]');
      if (customerData) {
        return JSON.parse(customerData.textContent);
      }
      
      return null;
    } catch (error) {
      console.log('No Shopify customer found');
      return null;
    }
  }

  /**
   * Login with Shopify customer account
   */
  async loginWithShopify(customer) {
    try {
      const response = await this.api.login(
        customer.id,
        customer.email,
        customer.firstName && customer.lastName 
          ? `${customer.firstName} ${customer.lastName}` 
          : customer.email
      );
      
      this.isAuthenticated = true;
      this.currentUser = response.user;
      
      document.dispatchEvent(new CustomEvent('wishlist:auth:success', {
        detail: { user: this.currentUser }
      }));
      
      return true;
    } catch (error) {
      console.error('Shopify login failed:', error);
      return false;
    }
  }

  /**
   * Send magic link for email-based auth
   */
  async sendMagicLink(email) {
    try {
      await this.api.sendMagicLink(email);
      return true;
    } catch (error) {
      console.error('Magic link failed:', error);
      throw error;
    }
  }

  /**
   * Verify existing token
   */
  async verifyToken() {
    try {
      // Try to get client match (optional - user might not have a match yet)
      try {
        await this.api.getClientMatch();
      } catch (matchError) {
        // Client match not found is OK - user can still use wishlist
        console.log('No client match found, but token is valid');
      }
      
      // If we have a token, user is authenticated
      if (this.api.getToken()) {
        this.isAuthenticated = true;
        return true;
      }
      
      return false;
    } catch (error) {
      // Token invalid or API error, clear it
      console.error('Token verification failed:', error);
      this.api.clearToken();
      this.isAuthenticated = false;
      return false;
    }
  }

  /**
   * Show authentication prompt modal
   */
  showAuthPrompt() {
    const modal = document.getElementById('wishlist-auth-modal');
    if (modal) {
      modal.classList.add('is-active');
      return;
    }

    // Create modal if it doesn't exist
    this.createAuthModal();
  }

  /**
   * Create authentication modal
   */
  createAuthModal() {
    const modal = document.createElement('div');
    modal.id = 'wishlist-auth-modal';
    modal.className = 'wishlist-modal';
    modal.innerHTML = `
      <div class="wishlist-modal__overlay" data-modal-close></div>
      <div class="wishlist-modal__content">
        <button class="wishlist-modal__close" data-modal-close aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2"/>
          </svg>
        </button>
        
          <div class="wishlist-auth">
          <h2 class="wishlist-auth__title">Sign in to your wishlist</h2>
          <p class="wishlist-auth__subtitle">Sign in to save items and access your wishlists across devices</p>
          <p class="wishlist-auth__reserve-member-note">
            <strong>Reserve Members:</strong> Use your login email and password from your Reserve Member Portal to sign in.
          </p>
          
          <div class="wishlist-auth__tabs">
            <button class="wishlist-auth__tab is-active" data-tab="login" type="button">Sign In</button>
            <button class="wishlist-auth__tab" data-tab="register" type="button">Create Account</button>
          </div>
          
          <!-- Login Form -->
          <form class="wishlist-auth__form" data-auth-form="login" style="display: block;">
            <div class="wishlist-auth__field">
              <label for="wishlist-login-email">Email address</label>
              <input 
                type="email" 
                id="wishlist-login-email" 
                name="email" 
                required 
                placeholder="your@email.com"
                class="input-full"
              />
            </div>
            <div class="wishlist-auth__field">
              <label for="wishlist-login-password">Password</label>
              <input 
                type="password" 
                id="wishlist-login-password" 
                name="password" 
                required 
                placeholder="Enter your password"
                class="input-full"
              />
            </div>
            <button type="submit" class="btn btn--primary btn--full">
              Sign In
            </button>
          </form>
          
          <!-- Register Form -->
          <form class="wishlist-auth__form" data-auth-form="register" style="display: none;">
            <div class="wishlist-auth__field">
              <label for="wishlist-register-email">Email address</label>
              <input 
                type="email" 
                id="wishlist-register-email" 
                name="email" 
                required 
                placeholder="your@email.com"
                class="input-full"
              />
            </div>
            <div class="wishlist-auth__field">
              <label for="wishlist-register-password">Password</label>
              <input 
                type="password" 
                id="wishlist-register-password" 
                name="password" 
                required 
                minlength="8"
                placeholder="At least 8 characters"
                class="input-full"
              />
            </div>
            <div class="wishlist-auth__field">
              <label for="wishlist-register-name">Name</label>
              <input 
                type="text" 
                id="wishlist-register-name" 
                name="name" 
                required
                autocomplete="name"
                placeholder="Your name"
                class="input-full"
              />
            </div>
            <div class="wishlist-auth__field">
              <label for="wishlist-register-company">Company (optional)</label>
              <input 
                type="text" 
                id="wishlist-register-company" 
                name="company" 
                placeholder="Your company"
                class="input-full"
              />
            </div>
            <div class="wishlist-auth__field">
              <label class="wishlist-auth__checkbox">
                <input 
                  type="checkbox" 
                  id="wishlist-register-reserve-member" 
                  name="isReserveMember"
                />
                <span>I am a registered Reserve Member</span>
              </label>
              <p class="wishlist-auth__help-text">Check this if you have an account in our Reserve Member system</p>
            </div>
            <button type="submit" class="btn btn--primary btn--full">
              Create Account
            </button>
          </form>
          
          <div class="wishlist-auth__message" data-auth-message style="display: none;"></div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Close modal handlers
    modal.querySelectorAll('[data-modal-close]').forEach(btn => {
      btn.addEventListener('click', () => {
        modal.classList.remove('is-active');
      });
    });

    // Tab switching
    const tabs = modal.querySelectorAll('[data-tab]');
    const loginForm = modal.querySelector('[data-auth-form="login"]');
    const registerForm = modal.querySelector('[data-auth-form="register"]');
    const message = modal.querySelector('[data-auth-message]');
    
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = tab.getAttribute('data-tab');
        tabs.forEach(t => t.classList.remove('is-active'));
        tab.classList.add('is-active');
        
        if (tabName === 'login') {
          loginForm.style.display = 'block';
          registerForm.style.display = 'none';
        } else {
          loginForm.style.display = 'none';
          registerForm.style.display = 'block';
        }
        message.style.display = 'none';
      });
    });

    // Login form handler
    loginForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = loginForm.querySelector('[name="email"]').value;
      const password = loginForm.querySelector('[name="password"]').value;
      
      try {
        const response = await this.api.loginEmailPassword(email, password);
        
        if (response.token) {
          this.isAuthenticated = true;
          this.currentUser = response.user;
          
          document.dispatchEvent(new CustomEvent('wishlist:auth:success', {
            detail: { user: response.user }
          }));
          
          // Close modal
          modal.classList.remove('is-active');
          
          // Only redirect if we're not on a collection/product page (where user might want to add items)
          // If on wishlist page, reload it
          if (window.location.pathname === '/pages/wishlist') {
            window.location.reload();
          } else if (!window.location.pathname.includes('/collections/') && !window.location.pathname.includes('/products/')) {
            // Redirect to wishlist page only if not on a product/collection page
            window.location.href = '/pages/wishlist';
          }
          // If on collection/product page, stay there so user can continue adding items
        }
      } catch (error) {
        console.error('Login error:', error);
        let errorMessage = error.message || 'Failed to sign in. Please check your email and password.';
        
        // Handle specific error cases
        if (error.message && error.message.includes('Not found')) {
          errorMessage = 'API endpoint not found. Please check that the backend is deployed and the API URL is correct.';
        } else if (error.message && error.message.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message && error.message.includes('Invalid email or password')) {
          errorMessage = 'Invalid email or password. Reserve Members: Use your Active Members password.';
        }
        
        message.textContent = errorMessage;
        message.style.display = 'block';
        message.className = 'wishlist-auth__message wishlist-auth__message--error';
      }
    });

    // Register form handler
    registerForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = registerForm.querySelector('[name="email"]').value;
      const password = registerForm.querySelector('[name="password"]').value;
      const name = registerForm.querySelector('[name="name"]').value.trim();
      const company = registerForm.querySelector('[name="company"]').value;
      const isReserveMember = registerForm.querySelector('[name="isReserveMember"]').checked;

      if (!name) {
        message.textContent = 'Name is required.';
        message.style.display = 'block';
        message.className = 'wishlist-auth__message wishlist-auth__message--error';
        return;
      }
      
      try {
        const response = await this.api.register(email, password, name, company, isReserveMember);
        
        if (response.token) {
          this.isAuthenticated = true;
          this.currentUser = response.user;
          
          document.dispatchEvent(new CustomEvent('wishlist:auth:success', {
            detail: { user: response.user }
          }));
          
          // Close modal
          modal.classList.remove('is-active');
          
          // Only redirect if we're not on a collection/product page (where user might want to add items)
          // If on wishlist page, reload it
          if (window.location.pathname === '/pages/wishlist') {
            window.location.reload();
          } else if (!window.location.pathname.includes('/collections/') && !window.location.pathname.includes('/products/')) {
            // Redirect to wishlist page only if not on a product/collection page
            window.location.href = '/pages/wishlist';
          }
          // If on collection/product page, stay there so user can continue adding items
        }
      } catch (error) {
        message.textContent = error.message || 'Failed to create account. Please try again.';
        message.style.display = 'block';
        message.className = 'wishlist-auth__message wishlist-auth__message--error';
      }
    });

    // Show modal
    modal.classList.add('is-active');
  }

  /**
   * Logout
   */
  logout() {
    this.api.clearToken();
    this.isAuthenticated = false;
    this.currentUser = null;
    
    document.dispatchEvent(new CustomEvent('wishlist:auth:logout'));
  }

  /**
   * Check if user is authenticated
   */
  checkAuth() {
    // Check if we have a token - if so, consider authenticated
    // The token itself is the source of truth
    const hasToken = this.api.getToken() !== null;
    
    // Update isAuthenticated flag if we have a token but flag wasn't set
    if (hasToken && !this.isAuthenticated) {
      this.isAuthenticated = true;
    }
    
    return hasToken;
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
// Initialize after WishlistAPI is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (window.WishlistAPI) {
      window.WishlistAuth = new WishlistAuth();
    } else {
      console.error('WishlistAPI not available when initializing WishlistAuth');
    }
  });
} else {
  if (window.WishlistAPI) {
    window.WishlistAuth = new WishlistAuth();
  } else {
    console.error('WishlistAPI not available when initializing WishlistAuth');
  }
}
});